import { useEffect, useState } from 'react';
import {
  CalendarDays,
  GraduationCap,
  Globe,
  Users,
  Phone,
  User,
  Utensils,
  Code,
  Printer,
  Download,
  Star,
} from 'lucide-react';

import ScanBadgeButton from '../../../components/scan-badge-button/scan-badge-button';

import { fetchUserRecord } from '../../../utils/mongoDataOperations';
import {
  formatDate,
  formatDateTime,
  calculateAge,
} from '../../../utils/helpers';
import { printBadge } from '../../../utils/badgePrint';
import { generateLaserBadgeSVG, downloadSVG } from '../../../utils/laserBadge';
import { userData } from '../../../assets/organizerList';

const Info = () => {
  const [scannedReturnedID, setScannedReturnedID] = useState(null);
  const [scannedUserRecord, setScannedUserRecord] = useState(null);

  const getOrganizerName = (email) => {
    const organizer = userData.users.find((user) => user.email === email);
    return organizer ? organizer.name : email;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (scannedReturnedID) {
        try {
          const response = await fetchUserRecord(scannedReturnedID);
          setScannedUserRecord(response);
        } catch (error) {
          console.error('Error fetching user record:', error);
        }
      }
    };
    fetchData();
  }, [scannedReturnedID]);

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className='flex items-center mb-4 space-x-3'>
      <div className='bg-gray-100 flex h-8 items-center justify-center rounded-full w-8'>
        <Icon className='h-4 text-gray-600 w-4' />
      </div>
      <div>
        <p className='text-gray-500 text-sm'>{label}</p>
        <p className='font-medium'>{value || '...'}</p>
      </div>
    </div>
  );

  const Card = ({ title, children, className }) => (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      {title && <h2 className='font-semibold mb-4 text-xl'>{title}</h2>}
      {children}
    </div>
  );

  const Badge = ({ children, variant = 'default' }) => {
    const variants = {
      default: 'bg-gray-100 text-gray-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${variants[variant]}`}>
        {children}
      </span>
    );
  };

  const formatMealCode = (code) => {
    if (!code) return '...';
    const [day, meal] = code.split('-');
    if (!meal) return day.charAt(0).toUpperCase() + day.slice(1);
    return `${day.charAt(0).toUpperCase() + day.slice(1)} ${
      meal.charAt(0).toUpperCase() + meal.slice(1)
    }`;
  };

  return (
    <div className='max-w-4xl mx-auto p-6 space-y-6'>
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center space-x-4'>
          <h1 className='font-bold text-3xl'>Hacker Info</h1>
          {scannedUserRecord?.application_info?.beginner_info && (
            <Badge variant='blue'>Beginner</Badge>
          )}
        </div>
        <div className='flex items-center space-x-3'>
          <button
            onClick={() => {
              if (!scannedReturnedID || !scannedUserRecord) return;
              // Use preferred name from API (already has fallback to Application name)
              const firstName = scannedUserRecord?.first_name || '';
              const lastName = scannedUserRecord?.last_name || '';
              printBadge(firstName, lastName, scannedReturnedID);
            }}
            disabled={!scannedReturnedID || !scannedUserRecord}
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              scannedReturnedID && scannedUserRecord
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            <Printer className='h-4 w-4' />
            <span>Print Badge</span>
          </button>
          <button
            onClick={async () => {
              if (!scannedReturnedID || !scannedUserRecord) return;
              const firstName = scannedUserRecord?.first_name || '';
              const lastName = scannedUserRecord?.last_name || '';
              const qrUrl = `https://trunk.treehacks.com/p/${scannedReturnedID}`;
              const svg = await generateLaserBadgeSVG(firstName, lastName, qrUrl);
              const fileName = `badge-${firstName.toLowerCase()}${lastName ? `-${lastName.toLowerCase()}` : ''}.svg`;
              downloadSVG(svg, fileName);
            }}
            disabled={!scannedReturnedID || !scannedUserRecord}
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              scannedReturnedID && scannedUserRecord
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}>
            <Download className='h-4 w-4' />
            <span>Laser Badge</span>
          </button>
          <button
            onClick={() => {
              setScannedUserRecord(null);
              setScannedReturnedID(null);
            }}
            className='border border-gray-300 hover:bg-gray-50 inline-flex items-center px-4 py-2 rounded-lg text-gray-700 transition-colors'>
            Clear
          </button>
          <ScanBadgeButton
            scannedReturnedID={scannedReturnedID}
            setScannedReturnedID={setScannedReturnedID}
          />
        </div>
      </div>

      <div className='gap-6 grid grid-cols-1 md:grid-cols-2'>
        <Card title='Personal Info'>
          <InfoItem
            icon={Code}
            label='TreeHacks ID'
            value={scannedReturnedID}
          />
          <InfoItem
            icon={User}
            label='Preferred Name'
            value={
              scannedUserRecord?.first_name
                ? `${scannedUserRecord.first_name}${scannedUserRecord.last_name ? ` ${scannedUserRecord.last_name}` : ''}`
                : '...'
            }
          />
          <InfoItem
            icon={User}
            label='Full Name (Government)'
            value={scannedUserRecord?.application_info?.full_name}
          />
          <InfoItem
            icon={Phone}
            label='Phone Number'
            value={scannedUserRecord?.application_info?.phone}
          />
          <InfoItem
            icon={CalendarDays}
            label='Date of Birth'
            value={formatDate(scannedUserRecord?.application_info?.dob)}
          />
          <InfoItem
            icon={CalendarDays}
            label='Age'
            value={
              scannedUserRecord?.application_info?.dob
                ? `${calculateAge(
                    scannedUserRecord?.application_info?.dob
                  )} years`
                : '...'
            }
          />
          <InfoItem
            icon={Users}
            label='Gender'
            value={scannedUserRecord?.application_info?.gender}
          />
        </Card>

        <Card title='Academic Info'>
          <InfoItem
            icon={GraduationCap}
            label='University'
            value={scannedUserRecord?.application_info?.university}
          />
          <InfoItem
            icon={Globe}
            label='Country'
            value={scannedUserRecord?.application_info?.country}
          />
          <InfoItem
            icon={CalendarDays}
            label='Graduation Year'
            value={scannedUserRecord?.application_info?.graduation_year}
          />
          <InfoItem
            icon={GraduationCap}
            label='Level of Study'
            value={scannedUserRecord?.application_info?.level_of_study}
          />

          {/* Highlights Section */}
          {scannedUserRecord?.highlights && scannedUserRecord.highlights.length > 0 && (
            <div className='mt-4 pt-4 border-t border-gray-200'>
              <div className='flex items-center mb-3'>
                <Star className='h-5 w-5 text-yellow-500 mr-2' />
                <h3 className='font-semibold text-gray-900'>Highlights</h3>
              </div>
              <ul className='space-y-2'>
                {scannedUserRecord.highlights.map((highlight, index) => (
                  <li
                    key={index}
                    className='flex items-start space-x-2 text-sm text-gray-700'>
                    <span className='text-yellow-500 mt-1'>•</span>
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card
          title='Event Info'
          className='md:col-span-2'>
          <div className='gap-6 grid grid-cols-1 md:grid-cols-4'>
            <div className='space-y-2'>
              <p className='text-gray-500 text-sm'>T-Shirt Size</p>
              <p className='font-medium'>
                {scannedUserRecord?.check_in_info?.tshirtSize || '...'}
              </p>
            </div>
            <div className='space-y-2'>
              <p className='text-gray-500 text-sm'>Check-in Status</p>
              <Badge
                variant={
                  scannedUserRecord?.check_in_info?.checkedIn
                    ? 'success'
                    : 'default'
                }>
                {scannedUserRecord?.check_in_info?.checkedIn
                  ? 'Checked In'
                  : 'Not Checked In'}
              </Badge>
            </div>
            {scannedUserRecord?.check_in_info?.checkedIn && (
              <>
                <div className='space-y-2'>
                  <p className='text-gray-500 text-sm'>Checked In At</p>
                  <p className='font-small'>
                    {formatDateTime(
                      scannedUserRecord?.check_in_info?.checkedInAt
                    ) || '...'}
                  </p>
                </div>
                <div className='space-y-2'>
                  <p className='text-gray-500 text-sm'>Checked In By</p>
                  <p className='font-small'>
                    {scannedUserRecord?.check_in_info?.checkedInBy
                      ? getOrganizerName(
                          scannedUserRecord.check_in_info.checkedInBy
                        )
                      : '...'}
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>

        <Card
          title='Meal Info'
          className='md:col-span-2'>
          <div className='space-y-6'>
            <InfoItem
              icon={Utensils}
              label='Dietary Restrictions'
              value={
                scannedUserRecord?.meal_info?.dietaryRestrictions || 'None'
              }
            />
            <div>
              <h3 className='font-medium mb-3'>Claimed Meals</h3>
              <div className='gap-4 grid grid-cols-1 md:grid-cols-3'>
                {scannedUserRecord?.meal_info?.usedMeals?.length > 0 ? (
                  scannedUserRecord.meal_info.usedMeals.map((meal, index) => (
                    <div
                      key={index}
                      className='bg-gray-50 flex items-center justify-between p-3 rounded-lg'>
                      <span className='text-sm'>{formatMealCode(meal)}</span>
                      <Badge variant='success'>Claimed</Badge>
                    </div>
                  ))
                ) : (
                  <p className='text-gray-500 text-sm'>No meals claimed yet</p>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card
          title='Workshop Info'
          className='md:col-span-2'>
          <div className='space-y-4'>
            <h3 className='font-medium'>Attended Workshops</h3>
            <div className='gap-4 grid grid-cols-1 md:grid-cols-3'>
              {scannedUserRecord?.workshop_info?.attendedWorkshops?.length >
              0 ? (
                scannedUserRecord.workshop_info.attendedWorkshops.map(
                  (workshop, index) => (
                    <div
                      key={index}
                      className='bg-gray-50 flex items-center justify-between p-3 rounded-lg'>
                      <span className='text-sm'>{workshop}</span>
                      <Badge variant='blue'>Attended</Badge>
                    </div>
                  )
                )
              ) : (
                <p className='text-gray-500 text-sm'>
                  No workshops attended yet
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card
          title='Hardware Info'
          className='md:col-span-2'>
          <div className='space-y-6'>
            <div className='gap-6 grid grid-cols-1 md:grid-cols-4'>
              <div className='space-y-2'>
                <p className='text-gray-500 text-sm'>Return Status</p>
                <Badge
                  variant={
                    scannedUserRecord?.hardware_info?.pendingReturn
                      ? 'warning'
                      : scannedUserRecord?.hardware_info?.returnedAt
                      ? 'success'
                      : 'default'
                  }>
                  {scannedUserRecord?.hardware_info?.pendingReturn
                    ? 'Pending Return'
                    : scannedUserRecord?.hardware_info?.returnedAt
                    ? 'Returned'
                    : 'No Hardware'}
                </Badge>
              </div>

              {scannedUserRecord?.hardware_info?.pendingReturn && (
                <>
                  <div className='space-y-2'>
                    <p className='text-gray-500 text-sm'>Checked Out By</p>
                    <p className='font-small'>
                      {scannedUserRecord?.hardware_info?.checkedOutBy
                        ? getOrganizerName(
                            scannedUserRecord.hardware_info.checkedOutBy
                          )
                        : '...'}
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <p className='text-gray-500 text-sm'>Checked Out At</p>
                    <p className='font-small'>
                      {formatDateTime(
                        scannedUserRecord?.hardware_info?.checkedOutAt
                      ) || '...'}
                    </p>
                  </div>
                </>
              )}

              {!scannedUserRecord?.hardware_info?.pendingReturn &&
                scannedUserRecord?.hardware_info?.returnedAt && (
                  <>
                    <div className='space-y-2'>
                      <p className='text-gray-500 text-sm'>Checked Out By</p>
                      <p className='font-small'>
                        {scannedUserRecord?.hardware_info?.checkedOutBy
                          ? getOrganizerName(
                              scannedUserRecord.hardware_info.checkedOutBy
                            )
                          : '...'}
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <p className='text-gray-500 text-sm'>Checked Out At</p>
                      <p className='font-small'>
                        {formatDateTime(
                          scannedUserRecord?.hardware_info?.checkedOutAt
                        ) || '...'}
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <p className='text-gray-500 text-sm'>Returned By</p>
                      <p className='font-small'>
                        {scannedUserRecord?.hardware_info?.returnedBy
                          ? getOrganizerName(
                              scannedUserRecord.hardware_info.returnedBy
                            )
                          : '...'}
                      </p>
                    </div>
                    <div className='space-y-2'>
                      <p className='text-gray-500 text-sm'>Returned At</p>
                      <p className='font-small'>
                        {formatDateTime(
                          scannedUserRecord?.hardware_info?.returnedAt
                        ) || '...'}
                      </p>
                    </div>
                  </>
                )}
            </div>

            <div className='space-y-2'>
              <p className='text-gray-500 text-sm'>Hardware List</p>
              <div className='bg-gray-50 p-3 rounded-lg'>
                <p className='whitespace-pre-wrap'>
                  {scannedUserRecord?.hardware_info?.hardwareList ||
                    'No hardware checked out'}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Info;
